import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { signOut } from 'firebase/auth'
import { auth } from '../../firebase/firebase'
import { useAuth } from '../../contexts/AuthContext'

const navItems = [
  { path: '/admin/pages',   label: 'Новини',     icon: '📰', permission: 'admin:pages' },
  { path: '/admin/archive', label: 'Архів',      icon: '🎬', permission: 'admin:archive' },
  { path: '/admin/users',   label: 'Користувачі', icon: '👤', permission: 'admin:users' },
  { path: '/admin/roles',   label: 'Ролі',        icon: '🔑', permission: 'admin:roles' },
]

const AdminLayout = ({ children }) => {
  const { userProfile } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const hasPermission = (permission) => {
    if (!userProfile) return false
    if (userProfile.permissions?.includes('admin:super')) return true
    return userProfile.permissions?.includes(permission)
  }

  const handleSignOut = async () => {
    await signOut(auth)
    navigate('/admin/login')
  }

  const initials = (userProfile?.displayName || userProfile?.email || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

  return (
    <div style={s.shell}>
      {/* Sidebar */}
      <aside style={s.sidebar}>
        <div style={s.brand}>
          <span style={s.brandMark}>✦</span>
          <span style={s.brandName}>Адмін</span>
        </div>

        <div style={s.sectionLabel}>Навігація</div>
        <nav style={s.nav}>
          {navItems.filter(item => hasPermission(item.permission)).map(item => {
            const active = location.pathname === item.path
            return (
              <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                <div style={{ ...s.navItem, ...(active ? s.navItemActive : {}) }}>
                  <span style={s.navIcon}>{item.icon}</span>
                  <span style={s.navLabel}>{item.label}</span>
                  {active && <span style={s.navPip} />}
                </div>
              </Link>
            )
          })}
        </nav>

        <div style={s.quickLinks}>
          <div style={s.sectionLabel}>Швидкі посилання</div>
          <a href='/admin/pages' style={s.quickLink}>/admin/pages</a>
          <a href='/admin/archive' style={s.quickLink}>/admin/archive</a>
          <a href='/admin/users' style={s.quickLink}>/admin/users</a>
          <a href='/admin/roles' style={s.quickLink}>/admin/roles</a>
        </div>

        <div style={s.userBox}>
          <div style={s.avatar}>{initials}</div>
          <div style={s.userInfo}>
            <div style={s.userName}>{userProfile?.displayName || 'Admin'}</div>
            <div style={s.userEmail}>{userProfile?.email}</div>
          </div>
          <button onClick={handleSignOut} style={s.signOutBtn} title='Вийти'>↩</button>
        </div>
      </aside>

      {/* Main */}
      <main style={s.main}>
        {children}
      </main>
    </div>
  )
}

const s = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#f5ede3',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  sidebar: {
    width: '240px',
    flexShrink: 0,
    background: '#2d1f14',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 0 24px',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '28px 24px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    marginBottom: '12px',
  },
  brandMark: {
    fontSize: '20px',
    color: '#c4986a',
  },
  brandName: {
    fontSize: '17px',
    fontWeight: '700',
    color: '#f0e6d8',
    letterSpacing: '0.5px',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#6b5040',
    padding: '0 24px',
    marginBottom: '6px',
    marginTop: '8px',
  },
  nav: {
    padding: '0 12px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginBottom: '2px',
    position: 'relative',
    transition: 'background 0.15s',
  },
  navItemActive: {
    background: 'rgba(196,152,106,0.15)',
  },
  navIcon: {
    fontSize: '16px',
    width: '20px',
    textAlign: 'center',
    flexShrink: 0,
  },
  navLabel: {
    fontSize: '14px',
    color: '#d4c0ae',
    fontWeight: '500',
    flex: 1,
  },
  navPip: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#c4986a',
    flexShrink: 0,
  },
  quickLinks: {
    marginTop: '24px',
    padding: '0 12px',
  },
  quickLink: {
    display: 'block',
    fontSize: '11px',
    color: '#c4a882',
    fontFamily: '"Courier New", monospace',
    padding: '4px 12px',
    marginBottom: '2px',
    textDecoration: 'none',
    borderRadius: '4px',
    transition: 'color 0.15s',
  },
  userBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    margin: '24px 12px 0',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  avatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #c4986a, #8b6044)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '700',
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  userName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#e8d8c8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userEmail: {
    fontSize: '11px',
    color: '#7a5c44',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  signOutBtn: {
    background: 'none',
    border: 'none',
    color: '#7a5c44',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
    lineHeight: 1,
    flexShrink: 0,
  },
  main: {
    flex: 1,
    padding: '36px 40px',
    overflowY: 'auto',
  },
}

export default AdminLayout
